/* eslint-disable react/prop-types */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Typography, Tooltip} from '@mui/material';
import {
    StepAudience,
    StepAutoresponders,
    StepCreationMethod,
    StepCompletedProfiles,
    StepExternalRecipients,
    StepFormAccess,
    StepFormApprovals,
    StepFormDetails1,
    StepFormDetails2,
    StepFormDetails3,
    StepFormEditor,
    StepFormType,
    StepGoLive,
    StepRecipientAssociation,
    StepRecipientBothParents,
    StepTemplateSelection,
    StepTriggerConditions
} from './CreateFormWizardSteps';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import {
    useCreateExternalUserFormAssignmentsMutation,
    useCreateFormEmployeeAssignmentMutation,
    useCreateFormQuestionAssignmentMutation,
    useCreateFormStudentAssignmentMutation,
} from '../../../../api/services/formAssignmentsService';
import {
    useCreateFormMutation,
    useGetFormTemplatesQuery,
    useLazyGetFormQuery,
    useUpdateFormMutation,
    useUpdateFormStatusMutation
} from '../../../../api/services/formsService';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import moment from 'moment';
import useNotification from '../../../../utility/hooks/useNotification';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import {useConfirm} from 'material-ui-confirm';
import {useNavigate} from 'react-router-dom';
import useUser from '../../../../utility/hooks/useUser';

const CreateFormWizard = ({
                              onFinish,
                              onBack,
                              selectedTemplate,
                          }) => {
    const mode = selectedTemplate?.mode ?? 'wizard';
    const isBlankStart = mode === 'blank';
    const [schemaValidation, setSchemaValidation] = useState({});
    const [step, setStep] = useState(0);
    const [formId, setFormId] = useState(null);
    const [componentToEditIndex, setComponentToEditIndex] = useState(undefined);

    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();

    const {user} = useUser();

    const schoolLogoSchemaComponent = useMemo(
        () => ({
            type: 'schoolLogo',
            url: user?.schoolLogoUrl
        }),
        [user?.schoolLogoUrl]
    );

    const initialState = {
        form: {
            type: null,
            audience: null,
        },

        template: {
            creationMethod: '',
            templateId: null,
            templateObject: null,
            categoryFilter: null,
            typeFilter: null,
        },

        details: {
            formName: '',
            expiryDate: '',
            goLiveDate: '',
            reminderFrequency: 0,
            allParentsCanSubmit: false,
            notificationEmail: [],
            categories: [],
            accessLevel: null,
            parentAccountType: null,
            strictRecipientsFiltering: false,
            allowMultipleSubmissions: false,
            enableRecipientNotifications: false,
            enableAutoresponder: false,
            isCommunication: selectedTemplate?.isCommunication === true,
            ProfileMustBeComplete: false
        },

        recipients: {
            assignmentType: null,
            student: {
                students: [],
                applications: [],
                enquiries: [],
            },
            employees: [],
            groups: [],
            externalEmails: [],
        },

        trigger: {
            selectedForm: null,
            selectedFormSchema: null,
            relatedComponent: null,
            targetState: null,
            externalEmails: [],
        },

        schema: []
    };

    const navigate = useNavigate();
    const [wizard, setWizard] = useState(initialState);
    const formAccessRef = useRef(null);
    const confirm = useConfirm();
    const [createForm] = useCreateFormMutation();
    const {data: formCategories = []} = useGetFormCategoriesQuery();
    const [createFormQuestionAssignment] = useCreateFormQuestionAssignmentMutation();
    const [createFormStudentAssignments] = useCreateFormStudentAssignmentMutation();
    const [createFormEmployeeAssignment] = useCreateFormEmployeeAssignmentMutation();
    const [createExternalUserFormAssignments] = useCreateExternalUserFormAssignmentsMutation();
    const {data: allTemplates = []} = useGetFormTemplatesQuery();
    const [updateForm] = useUpdateFormMutation();
    const [updateFormStatus] = useUpdateFormStatusMutation();
    const [getForm] = useLazyGetFormQuery();
    const templateSourceFromSelection = selectedTemplate?.source;
    const templateSourceFromWizard =
        wizard.template.creationMethod === 'schoolTemplate' ? 'school' : 'admicity';

    const templateSource = templateSourceFromSelection ?? templateSourceFromWizard;
    const MODE_HEADINGS = {
        wizard: 'Create Form Wizard',
        blank: wizard.details.isCommunication ? 'Send an Email' : 'Create Blank Form',
        template: templateSource === 'school'
            ? 'Use School Template'
            : 'Use an Admicity Template',
    };

    const modeHeading = MODE_HEADINGS[mode];
    const showStepInfo = !!wizard.form.type;
    const displayStep = step + 1;
    const totalStudentRecipients =
        wizard.recipients.student.students.length +
        wizard.recipients.student.applications.length +
        wizard.recipients.student.enquiries.length;
    const hasStudentRecipients = totalStudentRecipients > 0;
    const isBlankMode = mode === 'blank';

    const isBlankFormSelection =
        mode === 'wizard' && wizard.template.creationMethod === 'blank';

    const hasTemplateSelected =
        (!!wizard.template.templateId) &&
        (
            mode === 'template' ||
            (mode === 'wizard' &&
                (wizard.template.creationMethod === 'template' ||
                    wizard.template.creationMethod === 'schoolTemplate'))
        );

    const isEditorCreateFlow =
        hasTemplateSelected &&
        !isBlankMode &&
        !isBlankFormSelection &&
        wizard.form.type !== 'shareable';

    const hasRecipients = !!(
        wizard.recipients.student.students.length ||
        wizard.recipients.student.applications.length ||
        wizard.recipients.student.enquiries.length ||
        wizard.recipients.employees.length ||
        wizard.recipients.groups.length ||
        wizard.recipients.externalEmails.length
    );
    const stepSequence = useMemo(() => {
        const steps = [];

        const type = wizard.form.type;
        const audience = wizard.form.audience;

        const hasAutoBlankTemplate =
            wizard.template.creationMethod === 'blank' &&
            !!wizard.template.templateId;

        if (mode === 'blank') {
            steps.push('audience');

            if (!hasAutoBlankTemplate) {
                steps.push('templateSelection');
            }

            if (wizard.details.isCommunication) {

                steps.push('formDetails1');
            } else {

                steps.push('formDetails1', 'formDetails2', 'formDetails3');
            }
        } else if (mode === 'template') {
            steps.push('formType');

            if (type !== 'application' && type !== 'shareable') {
                steps.push('audience');
            }

            if (type === 'triggerForm') {
                if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
                    steps.push('externalRecipients');
                }

                steps.push(
                    'triggerConditions',
                    'templateSelection',
                    'formDetails1',
                    'formDetails2',
                    'formDetails3',
                    'formEditor'
                );
            } else {
                steps.push(
                    'templateSelection',
                    'formDetails1',
                    'formDetails2',
                    'formDetails3',
                    'formEditor'
                );
            }
        } else {
            steps.push('formType');

            if (type !== 'application' && type !== 'shareable') {
                steps.push('audience');
            }

            switch (type) {
                case 'triggerForm': {
                    if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
                        steps.push('externalRecipients');
                    }

                    steps.push('triggerConditions');
                    if (
                        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                        wizard.details.parentAccountType === 'with'
                    ) {
                        steps.push('completedProfiles');
                    }
                    steps.push('creationMethod');
                    const isTemplateBasedCreation =
                        wizard.template.creationMethod === 'template' ||
                        wizard.template.creationMethod === 'schoolTemplate';
                    if (isTemplateBasedCreation) {
                        steps.push('templateSelection');
                    }
                    steps.push('formDetails1', 'formDetails2', 'formDetails3');

                    if (hasTemplateSelected) {
                        steps.push('formEditor');
                    }

                    break;
                }

                case 'shareable': {
                    steps.push('creationMethod');

                    const isTemplateCreationMethod =
                        wizard.template.creationMethod === 'template' ||
                        wizard.template.creationMethod === 'schoolTemplate';

                    if (isTemplateCreationMethod) {
                        steps.push('templateSelection');
                    }

                    steps.push('formDetails1', 'formDetails2', 'formDetails3');
                    break;
                }

                case 'application': {
                    steps.push(
                        'creationMethod',
                        'templateSelection',
                        'formDetails1',
                        'formDetails2',
                        'formDetails3',
                        'formEditor'
                    );
                    break;
                }

                default: {
                    if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
                        steps.push('externalRecipients', 'recipientAssociation');
                    } else if (
                        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                        audience === FORM_AUDIENCE_TYPE.students ||
                        audience === FORM_AUDIENCE_TYPE.previousSchool ||
                        audience === FORM_AUDIENCE_TYPE.employees
                    ) {
                        steps.push('recipientAssociation');

                        if (
                            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                            type !== 'selfService'
                        ) {
                            steps.push('recipientBothParents');
                        }
                        if (
                            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                            wizard.details.parentAccountType === 'with'
                        ) {
                            steps.push('completedProfiles');
                        }
                    }

                    steps.push('creationMethod');

                    const isTemplateCreationMethod =
                        wizard.template.creationMethod === 'template' ||
                        wizard.template.creationMethod === 'schoolTemplate';

                    if (isTemplateCreationMethod) {
                        steps.push('templateSelection');
                    }

                    steps.push('formDetails1', 'formDetails2', 'formDetails3');

                    if (hasTemplateSelected) {
                        steps.push('formEditor');
                    }

                    break;
                }
            }
        }

        if (formId && mode !== 'template') {
            const type = wizard.form.type;

            const shouldIncludeFormEditorPostCreate =
                (mode === 'wizard' && type === 'shareable') ||
                (!hasTemplateSelected && type !== 'application');

            if (shouldIncludeFormEditorPostCreate) {
                steps.push('formEditor');
            }

            steps.push('formAccess');

            if (type !== 'application' && type !== 'shareable') {
                steps.push('formApprovals');
            }

            steps.push('formAutoresponders', 'formGoLive');
        }

        return steps;
    }, [
        mode,
        wizard.form.type,
        wizard.form.audience,
        wizard.template.creationMethod,
        wizard.template.templateId,
        formId,
        hasTemplateSelected
    ]);
    const totalSteps = stepSequence.length;
    const currentStepKey = stepSequence[step];
    const shouldCreateNow =
        (isEditorCreateFlow && currentStepKey === 'formEditor' && !formId) ||
        (!isEditorCreateFlow && !formId && (
            (wizard.details.isCommunication && currentStepKey === 'formDetails1') ||
            (!wizard.details.isCommunication && currentStepKey === 'formDetails3')
        ));

    const computeAccessLevel = (w) => {
        const templateType = w.template.templateObject?.type;
        const isParent = w.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;

        if (w.form.audience === FORM_AUDIENCE_TYPE.externalUsers) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (isParent && w.details.parentAccountType === 'without') {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (templateType === 'transition' || templateType === 'offer') {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (['acceptance', 'application', 'shareable'].includes(templateType)) {
            return FORM_ACCESS_LEVELS.public;
        }

        return FORM_ACCESS_LEVELS.private;
    };

    const accessLevel = useMemo(() => computeAccessLevel(wizard), [
        wizard.form.audience,
        wizard.details.parentAccountType,
        wizard.template.templateObject?.type
    ]);

    useEffect(() => {
        setWizard(prev => {
            if (prev.details.accessLevel === accessLevel) return prev;

            return {
                ...prev,
                details: {
                    ...prev.details,
                    accessLevel
                }
            };
        });
    }, [accessLevel]);

    const AssignmentType = () => {
        if (wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers) {
            if (wizard.recipients.assignmentType) {
                return wizard.recipients.assignmentType;
            }

            if (wizard.recipients.employees.length) {
                return FORM_ASSIGNMENT_TYPE.employees;
            }

            if (hasStudentRecipients) {
                return FORM_ASSIGNMENT_TYPE.students;
            }

            return FORM_ASSIGNMENT_TYPE.students;
        }

        switch (wizard.form.audience) {
            case FORM_AUDIENCE_TYPE.employees:
                return FORM_ASSIGNMENT_TYPE.employees;

            case FORM_AUDIENCE_TYPE.students:
            case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            case FORM_AUDIENCE_TYPE.previousSchool:
                return FORM_ASSIGNMENT_TYPE.students;

            default:
                return FORM_ASSIGNMENT_TYPE.none;
        }
    };
    const buildFormPayload = ({isCreate}) => {
        const accessLevel = computeAccessLevel(wizard);
        const assignmentTypeFinal = AssignmentType();
        let schema;

        if (isCreate) {
            const baseSchema = wizard.template.templateObject?.schema ?? [];
            const defaultSchema = [...baseSchema, schoolLogoSchemaComponent];

            schema =
                wizard.schema && wizard.schema.length
                    ? wizard.schema
                    : defaultSchema;
        } else {
            schema = wizard.schema;
        }

        return {
            ...(isCreate && {
                templateId: wizard.template.templateId,
            }),

            ...(!isCreate && {
                id: formId,
            }),

            schema,
            name: wizard.details.formName,
            remindInterval: wizard.details.reminderFrequency || 0,
            categoryIds: wizard.details.categories,
            strictRecipientsFiltering: wizard.details.strictRecipientsFiltering,
            allParentsCanSubmit: wizard.details.allParentsCanSubmit,
            allowMultipleSubmissions: wizard.details.allowMultipleSubmissions,
            enableRecipientNotifications: wizard.details.enableRecipientNotifications,
            audience: wizard.form.audience || 0,
            accessLevel,
            expirationDate: wizard.details.expiryDate || null,
            notificationEmails: wizard.details.notificationEmail,
            assignmentType: assignmentTypeFinal,
            scheduledActivationAt: wizard.details.goLiveDate || null,
            enableAutoresponder: wizard.details.enableAutoresponder,
            isCommunication: wizard.details.isCommunication,
            ProfileMustBeComplete: wizard.details.profileMustBeComplete
        };
    };
    const isFormEditorIncomplete = () => {
        const hasSchema =
            Array.isArray(wizard.schema) && wizard.schema.length > 0;

        const hasSchemaErrors =
            Object.values(schemaValidation).filter(Boolean).length !== 0;

        const hasName = !!wizard.details.formName;

        const hasReminder =
            wizard.details.reminderFrequency !== '' &&
            wizard.details.reminderFrequency !== null &&
            wizard.details.reminderFrequency !== undefined;

        return (
            !hasSchema ||
            hasSchemaErrors ||
            !accessLevel ||
            !hasName ||
            !hasReminder
        );
    };
    const isStepValid = () => {
        if (currentStepKey === 'formEditor') {
            return !isFormEditorIncomplete();
        }

        switch (currentStepKey) {
            case 'formType':
                return !!wizard.form.type;

            case 'triggerConditions': {
                const t = wizard.trigger;
                return (
                    t.selectedForm?.id &&
                    t.relatedComponent &&
                    t.targetState
                );
            }

            case 'audience':
                return wizard.form.audience !== null && wizard.form.audience !== undefined;

            case 'recipientAssociation':
                return wizard.details.allParentsCanSubmit !== null &&
                    wizard.details.allParentsCanSubmit !== undefined;

            case 'creationMethod':
                return !!wizard.template.creationMethod;

            case 'templateSelection':
                return !!wizard.template.templateId;

            case 'formDetails1':
                return !!wizard.details.formName;

            case 'formDetails2':
                return wizard.details.reminderFrequency !== '' &&
                    wizard.details.reminderFrequency !== null &&
                    wizard.details.reminderFrequency !== undefined;

            case 'formDetails3': {
                const expiry = wizard.details.expiryDate
                    ? moment(wizard.details.expiryDate)
                    : null;

                const activation = wizard.details.goLiveDate
                    ? moment(wizard.details.goLiveDate)
                    : null;

                return !(expiry && activation && expiry.isBefore(activation));
            }

            default:
                return true;
        }
    };
    const getFormEditorValidationMessage = () => {
        if (currentStepKey !== 'formEditor') return '';

        if (!wizard.details.formName) {
            return 'Please enter form name';
        }

        const reminder = wizard.details.reminderFrequency;
        if (reminder === '' || reminder === null || reminder === undefined) {
            return 'Please enter reminder frequency';
        }

        if (!accessLevel) {
            return 'Please select an appropriate access level / audience';
        }

        if (!Array.isArray(wizard.schema) || wizard.schema.length === 0) {
            return 'Please add at least one question or content block to the form';
        }

        const schemaErrors = Object.values(schemaValidation).filter(Boolean);
        if (schemaErrors.length) {
            return schemaErrors.join('; ');
        }

        return '';
    };

    const getFormById = async (id) => {
        return await getForm(id).unwrap();
    };
    const saveRecipients = async (formId) => {
        const {
            form: {audience},
            recipients: {
                assignmentType,
                student: {
                    students: studentFilters = [],
                    applications: applicationFilters = [],
                    enquiries: enquiryFilters = [],
                } = {},
                employees: employeeFilters = [],
                groups: groupFilters = [],
                externalEmails: externalUserEmailAssignments = [],
            },
        } = wizard;

        const tasks = [];

        const allStudentRecipients = [
            ...studentFilters,
            ...applicationFilters,
            ...enquiryFilters,
        ].map((x) => ({
            personId: x.id,
            name: x.label,
        }));

        const allEmployeeRecipients = employeeFilters.map((x) => ({
            personId: x.id,
            name: x.label || `${x.preferredForename ?? ''} ${x.preferredSurname ?? ''}`.trim(),
        }));

        const createdGroupFilters = groupFilters.map((x) => ({
            filterId: x.id,
            excluded: !!x.excluded,
        }));

        const externalEmails = externalUserEmailAssignments
            .filter((x) => !x.isDeleted)
            .map((x) => x.email);

        const shouldAssignStudents =
            [
                FORM_AUDIENCE_TYPE.students,
                FORM_AUDIENCE_TYPE.parentsForChildCompletion,
                FORM_AUDIENCE_TYPE.previousSchool,
            ].includes(audience) ||
            (audience === FORM_AUDIENCE_TYPE.externalUsers &&
                assignmentType === FORM_ASSIGNMENT_TYPE.students);

        const shouldAssignEmployees =
            audience === FORM_AUDIENCE_TYPE.employees ||
            (audience === FORM_AUDIENCE_TYPE.externalUsers &&
                assignmentType === FORM_ASSIGNMENT_TYPE.employees);

        const shouldAssignExternalUsers = audience === FORM_AUDIENCE_TYPE.externalUsers;

        if (shouldAssignStudents && (allStudentRecipients.length || createdGroupFilters.length)) {
            tasks.push({
                label: 'creating student/group assignments',
                action: () =>
                    createFormStudentAssignments({
                        formId,
                        personAssignments: allStudentRecipients,
                        groupFilters: createdGroupFilters,
                    }),
            });
        }

        if (shouldAssignEmployees && (allEmployeeRecipients.length || createdGroupFilters.length)) {
            tasks.push({
                label: 'creating employee/group assignments',
                action: () =>
                    createFormEmployeeAssignment({
                        formId,
                        personAssignments: allEmployeeRecipients,
                        groupFilters: createdGroupFilters,
                    }),
            });
        }

        if (shouldAssignExternalUsers && externalEmails.length) {
            tasks.push({
                label: 'creating external user assignments',
                action: () =>
                    createExternalUserFormAssignments({
                        formId,
                        emails: externalEmails,
                    }),
            });
        }

        if (!tasks.length) return;

        const errors = [];

        for (const task of tasks) {
            const result = await task.action();
            if (result?.error) {
                const reason =
                    result.error.data?.validationErrors?.map((x) => x.errorMessage).join('; ') ??
                    'Unknown error';
                errors.push({errorMessage: `Error while ${task.label}. ${reason}`});
            }
        }

        if (errors.length) {
            const error = new Error('Saving failed');
            error.type = 'SaveWizardRecipientsError';
            error.details = errors;
            throw error;
        }
    };
    const handleCreateForm = async () => {
        const trigger = wizard.trigger;
        const payload = buildFormPayload({isCreate: true});

        try {
            const res = await createForm(payload).unwrap();
            const newId = res?.formId || res?.id;

            await saveRecipients(newId);
            const form = await getFormById(newId);
            setWizard(prev => ({
                ...prev,
                schema: form.schema
            }));
            if (trigger?.selectedForm && trigger?.relatedComponent && trigger?.targetState) {
                const externalEmails =
                    wizard.trigger.externalEmails.filter(e => !e.isDeleted).map(e => e.email);

                await createFormQuestionAssignment({
                    formId: newId,
                    relatedFormId: trigger.selectedForm.id,
                    relatedComponentId: trigger.relatedComponent.id,
                    targetState: `${trigger.targetState}`,
                    displayName: `${trigger.selectedForm.name} - ${trigger.relatedComponent.description}`,
                    emails: externalEmails
                });
            }

            showSuccessNotification('Form created successfully!');
            return newId;

        } catch (err) {

            return null;
        }
    };
    const handleUpdateForm = async () => {
        try {
            const payload = buildFormPayload({isCreate: false});
            await updateForm(payload).unwrap();
            showSuccessNotification('Form saved successfully!');
        } catch (error) {

        }
    };
    const navigateBackToBeginning = () => {
        onFinish();
    };
    const handleMakeLive = async (id, formName) => {
        try {
            await confirm({
                title: 'Make Form Live',
                description: (
                    <>
                        Are you sure you want to make this form live? Email notifications will be sent to all assigned
                        recipients, and this action cannot be undone.
                        <br/><br/>
                        <strong>{formName}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
        } catch {
            return;
        }

        const result = await updateFormStatus({id});

        if (result.error) {
            showErrorNotification('Failed to publish form');
            return;
        }

        showSuccessNotification('Form published successfully');

        onFinish();
    };
    const handleSchedule = async (id, isoDate) => {
        const payload = buildFormPayload({isCreate: false});
        payload.id = id;
        payload.scheduledActivationAt = isoDate;

        try {
            await updateForm(payload).unwrap();
            showSuccessNotification('Form scheduled successfully');
            navigateBackToBeginning();
        } catch (e) {
            showErrorNotification('Failed to schedule form');
        }
    };

    const handleTemplateModeStart = () => {
        const preselectedTemplate = selectedTemplate?.templateObject ?? null;
        const preselectedTemplateId = selectedTemplate?.templateId ?? null;

        const initialCreationMethod =
            templateSource === 'school' ? 'schoolTemplate' : 'template';

        setWizard(() => ({
            ...initialState,
            form: {
                ...initialState.form,
                type: preselectedTemplate?.type ?? null,
                audience: null,
            },
            template: {
                ...initialState.template,
                creationMethod: initialCreationMethod,
                templateId: preselectedTemplateId,
                templateObject: preselectedTemplate,
                categoryFilter: preselectedTemplate?.category ?? null,
                typeFilter: preselectedTemplate?.type ?? null,
            },
            schema: preselectedTemplate?.schema
                ? [...preselectedTemplate.schema, schoolLogoSchemaComponent]
                : [],
        }));

        setStep(0);
    };

    const handleBlankModeStart = () => {
        const blank = allTemplates.find(
            t => t.category === 'Blank' && t.type === 'regular'
        );

        setWizard(prev => ({
            ...prev,
            form: {
                ...prev.form,
                type: 'regular',
                audience: null
            },
            template: {
                creationMethod: 'blank',
                templateId: blank?.id ?? null,
                templateObject: blank ?? null,
                categoryFilter: 'Blank',
                typeFilter: blank?.type ?? 'regular'
            },
            details: {
                ...prev.details,
                enableRecipientNotifications: true,
            },
            schema: [...(blank?.schema ?? []), schoolLogoSchemaComponent]
        }));

        setStep(0);
    };

    const handleTypeReset = (type) => {
        const allowMultiple = type === 'selfService';
        const enableNotifications = type !== 'noEmail';

        if (mode === 'blank') {
            return;
        }

        if (mode === 'wizard') {
            const isApplication = type === 'application';

            setWizard(prev => ({
                ...initialState,
                form: {
                    ...initialState.form,
                    type,
                    audience: null,
                },
                template: type === 'shareable'
                    ? prev.template
                    : {
                        ...initialState.template,
                        creationMethod: isApplication ? 'template' : '',
                    },
                details: {
                    ...initialState.details,
                    allowMultipleSubmissions: allowMultiple,
                    enableRecipientNotifications: enableNotifications,
                },
            }));
            setStep(0);
            return;
        }

        if (mode === 'template') {
            setWizard(prev => ({
                ...initialState,
                form: {
                    ...initialState.form,
                    type,
                    audience: null,
                },
                template: {
                    ...prev.template,
                    creationMethod: 'template',
                },
                details: {
                    ...initialState.details,
                    allowMultipleSubmissions: allowMultiple,
                    enableRecipientNotifications: enableNotifications,
                },
                schema: [],
            }));
            setStep(0);
        }
    };

    const handleAudienceReset = () => {
        setWizard(prev => ({
            ...prev,
            recipients: initialState.recipients,
            trigger: initialState.trigger,
            details: {
                ...initialState.details,
                parentAccountType: prev.details.parentAccountType ?? null,
                allowMultipleSubmissions: prev.details.allowMultipleSubmissions,
                enableRecipientNotifications: prev.details.enableRecipientNotifications,
            },
        }));
    };

    const autoAssignBlankTemplate = () => {
        if (!allTemplates.length) return;
        if (wizard.form.type === 'shareable') return;

        const audience = wizard.form.audience;
        if (audience == null) return;

        const creationMethod = wizard.template.creationMethod;

        const isBlankStartMode = mode === 'blank';
        const isWizardBlankCreation =
            mode === 'wizard' && creationMethod === 'blank';

        if (
            !isBlankStartMode &&
            !isWizardBlankCreation
        ) {
            return;
        }

        let requiredType = 'regular';

        if (audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            requiredType =
                wizard.details.parentAccountType === 'without'
                    ? 'offer'
                    : 'regular';
        } else if (audience === FORM_AUDIENCE_TYPE.previousSchool) {
            requiredType = 'transition';
        }

        const targetBlank =
            allTemplates.find(
                t => t.category === 'Blank' && t.type === requiredType && !t.schoolId
            ) ||
            allTemplates.find(
                t => t.category === 'Blank' && t.type === 'regular' && !t.schoolId
            );

        if (!targetBlank) return;

        setWizard(prev => ({
            ...prev,
            template: {
                creationMethod: 'blank',
                templateId: targetBlank.id,
                templateObject: targetBlank,
                categoryFilter: 'Blank',
                typeFilter: requiredType
            },
            schema: [...(targetBlank.schema ?? []), schoolLogoSchemaComponent]
        }));
    };
    const autoAssignShareableBlankTemplate = () => {
        if (mode !== 'wizard') return;
        if (wizard.form.type !== 'shareable') return;
        if (wizard.template.creationMethod !== 'blank') return;
        if (!allTemplates.length) return;
        if (wizard.template.templateId) return;

        const blankShareable =
            allTemplates.find(t =>
                t.category === 'Blank' &&
                t.type === 'shareable' &&
                !t.schoolId
            ) ||
            allTemplates.find(t =>
                t.category === 'Blank' &&
                t.type === 'shareable'
            );

        if (!blankShareable) return;

        setWizard(prev => ({
            ...prev,
            template: {
                ...prev.template,
                templateId: blankShareable.id,
                templateObject: blankShareable,
                categoryFilter: blankShareable.category,
                typeFilter: blankShareable.type,
            },
            schema: [...(blankShareable.schema ?? []), schoolLogoSchemaComponent],
        }));
    };

    const applyTemplateSchema = () => {
        if (!wizard.template.templateObject) return;

        setWizard(prev => ({
            ...prev,
            schema: [
                ...(wizard.template.templateObject.schema ?? []),
                schoolLogoSchemaComponent
            ]
        }));
    };

    useEffect(() => {
        if (!selectedTemplate) return;

        if (mode === 'template') {
            handleTemplateModeStart();
            return;
        }

        if (mode === 'blank') {
            handleBlankModeStart();
        }
    }, [mode, selectedTemplate, allTemplates, schoolLogoSchemaComponent]);

    useEffect(() => {
        const type = wizard.form.type;
        if (!type) return;

        if (mode === 'blank') return;

        handleTypeReset(type);
    }, [mode, wizard.form.type]);

    useEffect(() => {
        autoAssignBlankTemplate();
        autoAssignShareableBlankTemplate();
    }, [
        mode,
        wizard.form.type,
        wizard.form.audience,
        wizard.details.parentAccountType,
        wizard.template.creationMethod,
        allTemplates,
        schoolLogoSchemaComponent
    ]);

    useEffect(() => {
        const audience = wizard.form.audience;
        if (audience == null) return;

        if (mode === 'template') return;

        handleAudienceReset();
    }, [mode, wizard.form.audience]);

    useEffect(() => {
        const isWizardOrTemplateMode = mode === 'wizard' || mode === 'template';
        if (!isWizardOrTemplateMode) return;
        if (formId) return;

        applyTemplateSchema();
    }, [
        mode,
        formId,
        wizard.template.templateId,
        schoolLogoSchemaComponent
    ]);

    const getButtonsState = () => {
        const isBuilderStep = currentStepKey === 'formEditor';
        const formIsCreated = Boolean(formId);
        const showCreateButton = shouldCreateNow === true;
        const hideBackButton = isBuilderStep && formIsCreated;

        let nextLabel = 'Next';

        if (showCreateButton) {
            nextLabel = 'Create Form';
        } else if (isBuilderStep && formIsCreated) {
            nextLabel = 'Save & Continue';
        }

        return {
            isBuilderStep,
            formIsCreated,
            showCreateButton,
            hideBackButton,
            nextLabel
        };
    };
    const buttonsState = getButtonsState();
    const isFormEditorStep = currentStepKey === 'formEditor';
    const handleNextButton = async () => {
        const currentStepName = currentStepKey;

        if (shouldCreateNow) {
            if (!wizard.details.formName) return;

            const newId = await handleCreateForm();
            if (!newId) return;

            if (!isEditorCreateFlow && isBlankStart) {
                navigate(`/forms/edit/${newId}`);
                return;
            }
            if (mode === 'template') {
                navigate(`/forms/edit/${newId}`);
                return;
            }

            setFormId(newId);
            setWizard(prev => ({
                ...prev,
                createdFormId: newId
            }));

            if (!isEditorCreateFlow) {
                const builderIndex = stepSequence.indexOf('formEditor');
                if (builderIndex !== -1) {
                    setStep(builderIndex);
                } else {
                    setStep(prev => prev + 1);
                }
            } else {
                setStep(prev => prev + 1);
            }

            return;
        }

        if (currentStepName === 'formAccess') {
            if (formAccessRef.current?.saveAccessSettings) {
                await formAccessRef.current.saveAccessSettings();
            }
        }

        if (currentStepName === 'formEditor' && formId) {
            await handleUpdateForm();
        }

        if (currentStepName === 'formAutoresponders' && formId) {
            await handleUpdateForm();
        }

        setStep(prev => prev + 1);
    };

    const handleBackButton = () => {
        if (step === 0) {
            onBack('select');
            return;
        }

        const previousStepIndex = step - 1;

        if (previousStepIndex >= 0) {
            setStep(previousStepIndex);
        }
    };

    const components = {
        formType: (
            <StepFormType
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        triggerConditions: (
            <StepTriggerConditions
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        audience: (
            <StepAudience
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        externalRecipients: (
            <StepExternalRecipients
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        recipientAssociation: (
            <StepRecipientAssociation
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        recipientBothParents: (
            <StepRecipientBothParents
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        completedProfiles: (
            <StepCompletedProfiles
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        creationMethod: (
            <StepCreationMethod
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        templateSelection: (
            <StepTemplateSelection
                wizard={wizard}
                setWizard={setWizard}
                onSelected={() => setStep(prev => prev + 1)}
                audience={wizard.form.audience}
                templateSource={templateSource}
            />
        ),

        formDetails1: (
            <StepFormDetails1
                wizard={wizard}
                setWizard={setWizard}
                categories={formCategories}
            />
        ),

        formDetails2: (
            <StepFormDetails2
                wizard={wizard}
                setWizard={setWizard}
            />
        ),

        formDetails3: (
            <StepFormDetails3
                wizard={wizard}
                setWizard={setWizard}
                onCreate={handleCreateForm}
            />
        ),

        formEditor: (
            <StepFormEditor
                wizard={wizard}
                setWizard={setWizard}
                formId={formId}
                accessLevel={accessLevel}
                schema={wizard.schema}
                componentToEditIndex={componentToEditIndex}
                setComponentToEditIndex={setComponentToEditIndex}
                onSchemaChange={(next) =>
                    setWizard(prev => ({
                        ...prev,
                        schema: typeof next === 'function' ? next(prev.schema) : next
                    }))
                }
                onSchemaValidationChange={setSchemaValidation}
            />
        ),

        formAccess: (
            <StepFormAccess
                wizard={wizard}
                setWizard={setWizard}
                formId={formId}
                ref={formAccessRef}
            />
        ),

        formApprovals: (
            <StepFormApprovals
                formId={formId}
            />
        ),

        formAutoresponders: (
            <StepAutoresponders
                wizard={wizard}
                setWizard={setWizard}
                formId={formId}
            />
        ),

        formGoLive: (
            <StepGoLive
                wizard={wizard}
                formId={formId}
                hasRecipients={hasRecipients}
                onMakeLive={handleMakeLive}
                onSchedule={handleSchedule}
                onLater={navigateBackToBeginning}
                formName={wizard?.details.formName}
                mode={mode}
            />
        ),
    };
    const content = components[currentStepKey];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: '100%',
                overflow: 'hidden',
                backgroundColor: '#fff'
            }}
        >

            <Box
                sx={{
                    p: 3,
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#fff',
                }}
            >

                <Typography variant="h5" sx={{fontWeight: 600}}>
                    {modeHeading}
                </Typography>

                {showStepInfo && (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                        Step {displayStep} of {totalSteps}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                {content}
            </Box>

            <Box
                sx={{
                    borderTop: '1px solid #e0e0e0',
                    p: 2,
                    backgroundColor: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                {!buttonsState.hideBackButton
                    ? (
                        <Button variant="outlined" onClick={handleBackButton}>
                            Back
                        </Button>
                    )
                    : (
                        <Box/>
                    )}

                {currentStepKey !== 'templateSelection' &&
                    currentStepKey !== 'formGoLive' && (
                        isFormEditorStep
                            ? (
                                <Tooltip title={getFormEditorValidationMessage()}>
                    <span>
                        <Button
                            sx={{
                                backgroundColor: '#186BBE',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none'
                                }
                            }}
                            variant="contained"
                            disabled={!isStepValid()}
                            onClick={handleNextButton}
                        >
                            {buttonsState.nextLabel}
                        </Button>
                    </span>
                                </Tooltip>
                            )
                            : (
                                <Button
                                    sx={{
                                        backgroundColor: '#186BBE',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#155FA7',
                                            boxShadow: 'none'
                                        }
                                    }}
                                    variant="contained"
                                    disabled={!isStepValid()}
                                    onClick={handleNextButton}
                                >
                                    {buttonsState.nextLabel}
                                </Button>
                            )
                    )}
            </Box>
        </Box>
    );
};

export default CreateFormWizard;
